import React, { useEffect, useState } from "react";
import { Check, Search, UserPlus } from "lucide-react";
import { cn } from "../lib/api";
import { Button, Sheet } from "./ui";

/**
 * Pick club members and/or type guest names.
 * onDone receives [{ id, name }] for members and [{ name }] for guests.
 */
export default function PlayerPickerSheet({ open, onClose, title, members, onDone, priorityIds }) {
  const [selected, setSelected] = useState([]);
  const [guests, setGuests] = useState([]);
  const [guestName, setGuestName] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    if (open) { setSelected([]); setGuests([]); setGuestName(""); setQ(""); }
  }, [open]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const addGuest = () => {
    const name = guestName.trim();
    if (!name) return;
    setGuests((g) => [...g, name]);
    setGuestName("");
  };

  const coming = (m) => priorityIds?.has(m.id);
  const filtered = members
    .filter((m) => m.name.toLowerCase().includes(q.trim().toLowerCase()))
    .sort((a, b) => Number(coming(b) || 0) - Number(coming(a) || 0));
  const count = selected.length + guests.length;

  const done = () => {
    const picked = [
      ...members.filter((m) => selected.includes(m.id)).map((m) => ({ id: m.id, name: m.name, guest: !!m.guest })),
      ...guests.map((name) => ({ name })),
    ];
    onDone(picked);
  };

  return (
    <Sheet open={open} onClose={onClose} title={title}>
      <div className="space-y-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-1.5">Guest player</div>
          <form
            className="flex gap-2"
            onSubmit={(e) => { e.preventDefault(); addGuest(); }}
          >
            <input
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder="Not in the club? Type a name"
              maxLength={40}
              className="flex-1 h-11 px-3 rounded-2xl border border-black/10 outline-none focus:border-brand"
              data-testid="guest-name-input"
            />
            <Button type="submit" size="sm" className="h-11" variant="outline" data-testid="guest-add-btn">
              <UserPlus size={15} /> Add
            </Button>
          </form>
          {guests.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {guests.map((g, i) => (
                <button
                  key={i}
                  onClick={() => setGuests((gs) => gs.filter((_, j) => j !== i))}
                  className="h-8 px-3 rounded-xl bg-amber-50 text-amber-800 text-sm font-semibold"
                  title="Tap to remove"
                >
                  {g} ×
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-1.5">Club members</div>
          {members.length > 6 && (
            <div className="relative mb-2">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search"
                className="w-full h-10 pl-9 pr-3 rounded-2xl bg-black/[0.04] outline-none"
              />
            </div>
          )}
          {filtered.length === 0 ? (
            <div className="text-sm text-ink-muted py-3">No members available — add a guest above.</div>
          ) : (
            <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
              {filtered.map((m) => {
                const on = selected.includes(m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => toggle(m.id)}
                    className={cn(
                      "h-11 px-3 rounded-2xl text-sm font-semibold text-left truncate flex items-center gap-1.5",
                      on ? "bg-brand text-white" : "bg-black/[0.04] text-ink"
                    )}
                    data-testid={`pick-member-${m.id}`}
                  >
                    {on && <Check size={14} className="shrink-0" />}
                    <span className="truncate">{m.name}</span>
                    {coming(m) && !on && <span className="ml-auto text-[10px] font-bold text-brand shrink-0">IN</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <Button size="lg" className="w-full" onClick={done} disabled={count === 0} data-testid="picker-done-btn">
          Add {count || ""} player{count === 1 ? "" : "s"}
        </Button>
      </div>
    </Sheet>
  );
}
